import React, { useState, useEffect, useCallback } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
//@ts-ignore
import Modal from 'react-awesome-modal';
import { SvgElement, icontypesEnum } from '../assets/svgElement';

const formatAmount = (amount: any) => '₦' + Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

function LoanWallet() {
    const [wallet, setWallet] = useState<any>(null);
    const [metrics, setMetrics] = useState<any>(null);
    const [charges, setCharges] = useState<any[]>([]);
    const [pagination, setPagination] = useState<any>({ current_page: 1, last_page: 1 });
    const [search, setSearch] = useState('');
    const [loader, setLoader] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);

    // Withdraw modal
    const [withdrawModal, setWithdrawModal] = useState(false);
    const [amount, setAmount] = useState('');

    // Saved company accounts: picked, not typed. Verified with the payout
    // provider when saved, so a wrong digit is caught then, not by a transfer.
    const [accounts, setAccounts] = useState<any[]>([]);
    const [accountId, setAccountId] = useState<number | ''>('');
    const [accountModal, setAccountModal] = useState(false);
    const [editing, setEditing] = useState<any>(null);            // the account being edited, or null for new
    const [form, setForm] = useState({ label: '', bank_name: '', bank_code: '', account_number: '', is_default: false });
    const [savingAccount, setSavingAccount] = useState(false);

    // What this page shows below the tiles: rider deductions, or the wallet's own withdrawals.
    const [tab, setTab] = useState<'charges' | 'withdrawals' | 'exposure'>('charges');

    // The accounts behind the "rider negative exposure" figure. That number was a
    // total with nothing behind it — you could see what was owed but not by whom.
    const [exposure, setExposure] = useState<any[]>([]);
    const [exposureSummary, setExposureSummary] = useState<any>(null);
    const [ePagination, setEPagination] = useState<any>({ current_page: 1, last_page: 1 });
    const [eLoader, setELoader] = useState(false);
    const [eSearch, setESearch] = useState('');
    const [withdrawals, setWithdrawals] = useState<any[]>([]);
    const [wPagination, setWPagination] = useState<any>({ current_page: 1, last_page: 1 });
    const [wLoader, setWLoader] = useState(false);

    const loadAccounts = useCallback(() => {
        SuperAdminApis.getLoanWalletBankAccounts().then((res: AxiosResponse<any>) => {
            const list = res?.data?.data || [];
            setAccounts(list);
            // Preselect the default so the common case is amount → withdraw.
            setAccountId((prev) => prev || (list.find((a: any) => a.is_default)?.id ?? list[0]?.id ?? ''));
        }).catch(() => {});
    }, []);

    const loadExposure = useCallback((page?: number) => {
        setELoader(true);
        SuperAdminApis.getLoanWalletNegativeExposure({ page: page || 1, per_page: 15, search: eSearch })
            .then((res: AxiosResponse<any>) => {
                if (res?.data?.status) {
                    setExposure(res.data.data.items || []);
                    setExposureSummary(res.data.data.summary || null);
                    setEPagination(res.data.data.pagination || { current_page: 1, last_page: 1 });
                }
            })
            .catch(() => { })
            .finally(() => setELoader(false));
    }, [eSearch]);

    const loadWithdrawals = useCallback((page?: number) => {
        setWLoader(true);
        SuperAdminApis.getLoanWalletWithdrawals({ page: page || 1, per_page: 15 }).then((res: AxiosResponse<any>) => {
            setWLoader(false);
            const d = res?.data?.data;
            if (d) {
                setWithdrawals(d.data || []);
                setWPagination({ current_page: d.current_page, last_page: d.last_page });
            }
        }).catch(() => { setWLoader(false); toast.error('Could not load the withdrawal history.'); });
    }, []);

    const openAccountForm = (a: any = null) => {
        setEditing(a);
        setForm(a
            ? { label: a.label, bank_name: a.bank_name, bank_code: a.bank_code, account_number: a.account_number, is_default: !!a.is_default }
            : { label: '', bank_name: '', bank_code: '', account_number: '', is_default: accounts.length === 0 });
        setAccountModal(true);
    };

    const saveAccount = () => {
        if (!form.label.trim() || !form.bank_name.trim() || !form.bank_code.trim() || form.account_number.trim().length !== 10) {
            toast.error('Fill in the label, bank, bank code and a 10-digit account number.');
            return;
        }
        setSavingAccount(true);
        SuperAdminApis.saveLoanWalletBankAccount(form, editing?.id).then((res: AxiosResponse<any>) => {
            setSavingAccount(false);
            if (res?.data?.status) {
                toast.success(res.data.message, { autoClose: 6000 });
                setAccountModal(false);
                loadAccounts();
                if (!editing) setAccountId(res.data.data?.id ?? '');
            } else {
                toast.error(res?.data?.message || 'Could not save the account.');
            }
        }).catch((err: any) => {
            setSavingAccount(false);
            toast.error(err?.response?.data?.message || 'Could not save the account.', { autoClose: 8000 });
        });
    };

    const removeAccount = (a: any) => {
        if (!window.confirm(`Remove "${a.label}" (${a.bank_name} ${a.account_number})? Past withdrawals keep their details.`)) return;
        SuperAdminApis.deleteLoanWalletBankAccount(a.id).then((res: AxiosResponse<any>) => {
            if (res?.data?.status) { toast.success(res.data.message); if (accountId === a.id) setAccountId(''); loadAccounts(); }
            else toast.error(res?.data?.message || 'Could not remove the account.');
        }).catch((err: any) => toast.error(err?.response?.data?.message || 'Could not remove the account.'));
    };

    const loadOverview = useCallback((page?: number) => {
        setLoader(true);
        SuperAdminApis.getLoanWalletOverview({ search: search || undefined, page: page || 1, per_page: 15 })
            .then((res: AxiosResponse<any>) => {
                setLoader(false);
                if (res?.data?.status) {
                    setWallet(res.data.data.wallet);
                    setMetrics(res.data.data.metrics);
                    setCharges(res.data.data.charges?.items || []);
                    setPagination(res.data.data.charges?.pagination || { current_page: 1, last_page: 1 });
                }
            }).catch(() => { setLoader(false); toast.error('Failed to load loan wallet.'); });
    }, [search]);

    useEffect(() => { loadOverview(); loadAccounts(); loadWithdrawals(); }, []);

    const resetWithdraw = () => { setAmount(''); };

    const submitWithdraw = () => {
        if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a valid amount.'); return; }
        if (parseFloat(amount) > Number(wallet?.available_balance || 0)) { toast.error('Amount exceeds available balance.'); return; }
        if (!accountId) { toast.error('Choose the company account to pay into.'); return; }

        setActionLoader(true);
        SuperAdminApis.loanWalletWithdraw({
            amount: parseFloat(amount),
            bank_account_id: accountId,
        }).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            if (res?.data?.status) {
                toast.success(res.data.message || 'Withdrawal initiated.', { autoClose: 6000 });
                setWithdrawModal(false); resetWithdraw(); loadOverview();
                setTab('withdrawals'); loadWithdrawals();
            } else {
                toast.error(res?.data?.message || 'Withdrawal failed.');
            }
        }).catch((err: any) => {
            setActionLoader(false);
            toast.error(err?.response?.data?.message || 'Withdrawal failed.');
        });
    };

    return (
        <>
            <AdminSidebar title="Loan Wallet" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Loan Wallet" />
                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">
                    <div className="flex justify-between items-center mt-5 mb-2">
                        <div>
                            <h3 className="text-[15px] text-[#1C1C1C] font-semibold">Loan Recovery Wallet</h3>
                            <p className="text-xs text-gray-500">All loan repayments deducted from riders collect here. Withdraw to a company account when needed.</p>
                        </div>
                        <button onClick={() => { resetWithdraw(); setWithdrawModal(true); }} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">Withdraw</button>
                    </div>

                    {/* Balance + metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-500">Available (withdrawable)</p>
                            <p className="text-xl font-bold text-green-600">{formatAmount(wallet?.available_balance)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-500">Total recovered</p>
                            <p className="text-xl font-bold text-gray-800">{formatAmount(wallet?.total_recovered)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-500">Total withdrawn</p>
                            <p className="text-xl font-bold text-gray-800">{formatAmount(wallet?.total_withdrawn)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-500">Active loans</p>
                            <p className="text-xl font-bold text-blue-600">{metrics?.active_loans ?? 0}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-500">Total loaned</p>
                            <p className="text-lg font-bold text-gray-800">{formatAmount(metrics?.total_loaned)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-500">Total repaid</p>
                            <p className="text-lg font-bold text-gray-800">{formatAmount(metrics?.total_repaid)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-500">Outstanding (loans)</p>
                            <p className="text-lg font-bold text-gray-800">{formatAmount(metrics?.total_outstanding)}</p>
                        </div>
                        <button type="button" onClick={() => { setTab('exposure'); loadExposure(); }}
                            className="bg-white rounded-lg p-4 shadow-sm border border-amber-200 text-left hover:ring-2 hover:ring-amber-300 transition">
                            <p className="text-xs text-gray-500">Rider negative exposure</p>
                            <p className="text-lg font-bold text-amber-600">{formatAmount(metrics?.rider_negative_exposure)}</p>
                            <p className="text-[10px] text-gray-400">still owed, not yet real cash — click to see who</p>
                        </button>
                    </div>

                    {/* Rider deductions | wallet withdrawals */}
                    <div className="flex gap-2 mb-3">
                        {[['charges', 'Rider deductions'], ['withdrawals', 'Withdrawals from this wallet'], ['exposure', 'Wallets owing']].map(([k, label]) => (
                            <button key={k} onClick={() => { setTab(k as any); if (k === 'withdrawals') loadWithdrawals(); if (k === 'exposure') loadExposure(); }}
                                className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === k ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 border'}`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    {tab === 'exposure' && (
                    <div className="bg-white rounded-lg p-4 shadow-sm mb-3">
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                            <p className="text-sm font-semibold">Wallets owing</p>
                            <button onClick={() => loadExposure(ePagination.current_page)} className="text-xs text-gray-500 hover:text-gray-800">Refresh</button>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                            {exposureSummary
                                ? `${exposureSummary.wallets} wallet(s) carrying ${formatAmount(exposureSummary.exposure)} of debt. None of them can withdraw until the balance is back above zero.`
                                : 'Accounts with a negative balance.'}
                        </p>

                        <div className="flex gap-2 mb-3">
                            <input
                                value={eSearch}
                                onChange={(e) => setESearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') loadExposure(1); }}
                                placeholder="Name / email…"
                                className="border rounded-md px-3 py-2 text-sm flex-1"
                            />
                            <button onClick={() => loadExposure(1)} className="bg-[#E04343] text-white text-sm px-4 py-2 rounded-md">Filter</button>
                        </div>

                        {eLoader && <div className="flex justify-center py-6"><Oval visible height="30" width="30" color="#E04343" secondaryColor="#eee" ariaLabel="loading" /></div>}

                        <div className="relative overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-gray-600 uppercase bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3">Account</th>
                                        <th className="px-4 py-3">Type</th>
                                        <th className="px-4 py-3 text-right">Balance</th>
                                        <th className="px-4 py-3 text-right">Fines</th>
                                        <th className="px-4 py-3 text-right">Loans</th>
                                        <th className="px-4 py-3 text-right">Carried over</th>
                                        <th className="px-4 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!eLoader && exposure.length === 0 && (
                                        <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No wallet is owing.</td></tr>
                                    )}
                                    {exposure.map((r: any) => (
                                        <tr key={r.wallet_id} className="bg-white border-b">
                                            <td className="px-4 py-3">
                                                <p className="font-semibold text-gray-800">{r.name || '—'}</p>
                                                <p className="text-xs text-gray-500">{r.email}</p>
                                            </td>
                                            <td className="px-4 py-3 capitalize text-gray-600">{r.role || '—'}</td>
                                            <td className="px-4 py-3 text-right font-semibold text-amber-600">{formatAmount(r.balance)}</td>
                                            {/* Lifetime totals per cause. They usually exceed the balance,
                                                because earnings since have paid part of it back. */}
                                            <td className="px-4 py-3 text-right text-gray-700">{r.fines ? formatAmount(r.fines) : '—'}</td>
                                            <td className="px-4 py-3 text-right text-gray-700">{r.loans ? formatAmount(r.loans) : '—'}</td>
                                            <td className="px-4 py-3 text-right text-gray-700">{r.carried ? formatAmount(r.carried) : '—'}</td>
                                            <td className="px-4 py-3">
                                                <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                    {r.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {ePagination.last_page > 1 && (
                            <div className="flex items-center justify-between mt-3 text-sm">
                                <button disabled={ePagination.current_page <= 1}
                                    onClick={() => loadExposure(ePagination.current_page - 1)}
                                    className="px-3 py-1 border rounded disabled:opacity-40">Previous</button>
                                <span className="text-gray-500">Page {ePagination.current_page} of {ePagination.last_page}</span>
                                <button disabled={ePagination.current_page >= ePagination.last_page}
                                    onClick={() => loadExposure(ePagination.current_page + 1)}
                                    className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
                            </div>
                        )}
                    </div>
                    )}

                    {tab === 'withdrawals' && (
                    <div className="bg-white rounded-lg p-4 shadow-sm mb-3">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold">Withdrawals from this wallet</p>
                            <button onClick={() => loadWithdrawals(wPagination.current_page)} className="text-xs text-gray-500 hover:text-gray-800">Refresh</button>
                        </div>
                        {wLoader && <div className="flex justify-center py-6"><Oval visible height="30" width="30" color="#E04343" secondaryColor="#eee" ariaLabel="loading" /></div>}
                        <div className="relative overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                                <thead className="text-[11px] text-[#030229] bg-[#FAFAFA] uppercase">
                                    <tr>
                                        <th className="px-2 py-2">Reference</th>
                                        <th className="px-2 py-2">Amount</th>
                                        <th className="px-2 py-2">Paid to</th>
                                        <th className="px-2 py-2">Via</th>
                                        <th className="px-2 py-2">Status</th>
                                        <th className="px-2 py-2">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {withdrawals.length > 0 ? withdrawals.map((w) => (
                                        <tr key={w.id} className="bg-white">
                                            <td className="px-2 py-3 text-xs font-mono">{w.reference}</td>
                                            <td className="px-2 py-3 text-xs font-semibold">{formatAmount(w.amount)}</td>
                                            <td className="px-2 py-3 text-xs">
                                                <div>{w.bank_name}</div>
                                                <div className="text-[10px] text-gray-400">{w.account_number} · {w.account_name}</div>
                                            </td>
                                            <td className="px-2 py-3 text-xs capitalize">{w.provider || 'paystack'}</td>
                                            <td className="px-2 py-3 text-xs">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                                    w.status === 'completed' ? 'bg-green-100 text-green-700'
                                                        : w.status === 'failed' ? 'bg-red-100 text-red-700'
                                                            : 'bg-yellow-100 text-yellow-700'}`}>{w.status}</span>
                                                {w.status === 'failed' && w.failure_reason && (
                                                    <div className="text-[10px] text-red-500 mt-0.5 max-w-[16rem]">{w.failure_reason}</div>
                                                )}
                                            </td>
                                            <td className="px-2 py-3 text-xs">
                                                <div>{w.created_at ? new Date(w.created_at).toLocaleString() : '—'}</div>
                                                {w.completed_at && <div className="text-[10px] text-gray-400">done {new Date(w.completed_at).toLocaleString()}</div>}
                                            </td>
                                        </tr>
                                    )) : !wLoader && (
                                        <tr><td colSpan={6} className="text-center py-8 text-gray-400">No withdrawals from this wallet yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {wPagination.last_page > 1 && (
                            <div className="flex justify-end mt-4 gap-1">
                                <button disabled={wPagination.current_page <= 1} onClick={() => loadWithdrawals(wPagination.current_page - 1)} className="py-1 px-3 text-xs bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 rounded">Prev</button>
                                <span className="py-1 px-3 text-xs">Page {wPagination.current_page} of {wPagination.last_page}</span>
                                <button disabled={wPagination.current_page >= wPagination.last_page} onClick={() => loadWithdrawals(wPagination.current_page + 1)} className="py-1 px-3 text-xs bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 rounded">Next</button>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Charge history */}
                    {tab === 'charges' && (
                    <div className="bg-white rounded-lg p-4 shadow-sm mb-3">
                        <div className="flex flex-wrap gap-3 items-end mb-3">
                            <div className="flex-1 min-w-[150px]">
                                <label className="text-xs text-gray-500">Search rider</label>
                                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name / email..." className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <button onClick={() => loadOverview()} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">Filter</button>
                        </div>

                        {loader && <div className="flex justify-center py-6"><Oval visible height="30" width="30" color="#E04343" secondaryColor="#eee" ariaLabel="loading" /></div>}
                        <div className="relative overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                                <thead className="text-[11px] text-[#030229] bg-[#FAFAFA] uppercase">
                                    <tr>
                                        <th className="px-2 py-2">Rider</th>
                                        <th className="px-2 py-2">Cycle</th>
                                        <th className="px-2 py-2">Amount</th>
                                        <th className="px-2 py-2">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {charges.length > 0 ? charges.map((c) => (
                                        <tr key={c.id} className="bg-white">
                                            <td className="px-2 py-3 text-xs"><div className="font-semibold">{c.rider || '—'}</div><div className="text-[10px] text-gray-400">{c.rider_email}</div></td>
                                            <td className="px-2 py-3 text-xs">{c.cycle_number}</td>
                                            <td className="px-2 py-3 text-xs font-semibold">{formatAmount(c.amount)}</td>
                                            <td className="px-2 py-3 text-xs">{c.charged_at ? new Date(c.charged_at).toLocaleString() : '—'}</td>
                                        </tr>
                                    )) : !loader && (
                                        <tr><td colSpan={4} className="text-center py-8 text-gray-400">No deductions yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {pagination.last_page > 1 && (
                            <div className="flex justify-end mt-4 gap-1">
                                <button disabled={pagination.current_page <= 1} onClick={() => loadOverview(pagination.current_page - 1)} className="py-1 px-3 text-xs bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 rounded">Prev</button>
                                <span className="py-1 px-3 text-xs">Page {pagination.current_page} of {pagination.last_page}</span>
                                <button disabled={pagination.current_page >= pagination.last_page} onClick={() => loadOverview(pagination.current_page + 1)} className="py-1 px-3 text-xs bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 rounded">Next</button>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            </div>

            {/* Withdraw Modal */}
            <Modal visible={withdrawModal} width="480" height="520" effect="fadeInUp" onClickAway={() => setWithdrawModal(false)}>
                <div className="p-5 overflow-y-auto max-h-[510px]">
                    <div className="flex justify-between mb-4">
                        <p className="font-bold">Withdraw from Loan Wallet</p>
                        <p className="cursor-pointer" onClick={() => setWithdrawModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Available: <strong>{formatAmount(wallet?.available_balance)}</strong></p>

                    <div className="mb-3">
                        <label className="text-xs text-gray-600 mb-1 block">Amount (₦)</label>
                        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                    </div>
                    <div className="mb-2">
                        <label className="text-xs text-gray-600 mb-1 block">Pay into</label>
                        {accounts.length === 0 ? (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                                No company account saved yet. Add one below — it is verified with the bank when saved.
                            </p>
                        ) : (
                            <select value={accountId} onChange={(e) => setAccountId(Number(e.target.value))}
                                className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white">
                                {accounts.map((a) => (
                                    <option key={a.id} value={a.id}>{a.label} — {a.bank_name} {a.account_number} ({a.account_name}){a.is_default ? ' · default' : ''}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Manage the saved accounts without leaving the modal. */}
                    <div className="mb-4 border rounded-md divide-y">
                        {accounts.map((a) => (
                            <div key={a.id} className="flex items-center justify-between px-2 py-1.5 text-xs">
                                <span><strong>{a.label}</strong> <span className="text-gray-400">{a.bank_name} · {a.account_number}</span></span>
                                <span className="flex gap-2">
                                    <button onClick={() => openAccountForm(a)} className="text-blue-600 hover:underline">Edit</button>
                                    <button onClick={() => removeAccount(a)} className="text-red-500 hover:underline">Remove</button>
                                </span>
                            </div>
                        ))}
                        <button onClick={() => openAccountForm(null)} className="w-full text-left px-2 py-1.5 text-xs text-[#E04343] font-semibold hover:bg-gray-50">
                            + Add a company account
                        </button>
                    </div>

                    <button onClick={submitWithdraw} disabled={actionLoader || !accountId} className="bg-[#E04343] text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                        Initiate Withdrawal
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* Add / edit a company account */}
            <Modal visible={accountModal} width="420" height="400" effect="fadeInUp" onClickAway={() => setAccountModal(false)}>
                <div className="p-5 overflow-y-auto max-h-[390px]">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">{editing ? 'Edit company account' : 'Add a company account'}</p>
                        <p className="cursor-pointer" onClick={() => setAccountModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-3">
                        The account is checked with the bank when you save; the account name is filled in from the bank's own record.
                    </p>
                    <div className="mb-2">
                        <label className="text-xs text-gray-600 mb-1 block">Label</label>
                        <input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Main GTB account" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-2">
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block">Bank name</label>
                            <input type="text" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block">Bank code</label>
                            <input type="text" value={form.bank_code} onChange={(e) => setForm({ ...form, bank_code: e.target.value })} placeholder="e.g. 058" className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                        </div>
                    </div>
                    <div className="mb-2">
                        <label className="text-xs text-gray-600 mb-1 block">Account number</label>
                        <input type="text" maxLength={10} value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, '') })} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-700 mb-4">
                        <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                        Use as the default account
                    </label>
                    <button onClick={saveAccount} disabled={savingAccount} className="bg-[#E04343] text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                        {savingAccount ? 'Verifying with the bank…' : (editing ? 'Save changes' : 'Verify and save')}
                        <Oval visible={savingAccount} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
        </>
    );
}

export default LoanWallet;
